import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const SkinRevitalization = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const benefits = [
    {
      title: 'Enhanced Collagen Stimulation',
      description: 'Triggers natural collagen production for firmer, more youthful-looking skin over time.'
    },
    {
      title: 'Reduction in Fine Lines and Wrinkles',
      description: 'Smooths fine lines and softens wrinkles for a more youthful, refreshed appearance.'
    },
    {
      title: 'Improved Skin Texture and Tone',
      description: 'Refines skin texture and evens out tone for a smoother, more radiant complexion.'
    },
    {
      title: 'Tightens Pores',
      description: 'Minimizes enlarged pores for a refined, polished skin surface.'
    },
    {
      title: 'Reduces Photodamage',
      description: 'Addresses sun damage, age spots, and discoloration for clearer, more even skin.'
    },
    {
      title: 'Minimal Downtime',
      description: 'Quick recovery with minimal side effects, allowing you to resume activities quickly.'
    },
    {
      title: 'Suitable for Various Skin Types',
      description: 'Safe and effective for most skin types and tones with customizable treatment parameters.'
    },
    {
      title: 'Visible Results Over Time',
      description: 'Progressive improvements as skin naturally rejuvenates and repairs itself.'
    },
    {
      title: 'Quick Treatment Sessions',
      description: 'Efficient treatments that fit into busy schedules with lasting results.'
    },
    {
      title: 'Safe and Effective',
      description: 'Proven technology with minimal risks and complications for skin rejuvenation.'
    },
    {
      title: 'Comprehensive Facial Revitalization',
      description: 'Addresses multiple skin concerns in one treatment for overall improvement.'
    },
    {
      title: 'Enhances Overall Skin Appearance',
      description: 'Creates a more vibrant, healthy-looking complexion with improved clarity and glow.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Moveo Glo Laser Facial - Skin Revitalization | Century Aesthetics</title>
        <meta name="description" content="Moveo Glo laser facial for skin rejuvenation, collagen stimulation, and anti-aging. Reduce wrinkles and improve texture in Forest Hills, NY." />
        <meta name="keywords" content="moveo glo facial, laser facial, skin revitalization, laser rejuvenation, anti-aging facial, collagen stimulation" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/skin-revitalization" />
        
        <meta property="og:title" content="Moveo Glo Laser Facial | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Moveo Glo laser facial for skin rejuvenation, collagen stimulation, and anti-aging. Reduce wrinkles and improve texture in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/skin-revitalization" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">
    
      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/skin-revitalization.jpg" 
                  alt="Moveo Glo Facial Treatment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                MOVEO GLO FACIAL FOR RADIANT SKIN REJUVENATION
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The Moveo Glo Facial is an advanced laser skin revitalization treatment that delivers comprehensive facial rejuvenation through controlled light energy. This non-invasive procedure stimulates collagen production, reduces fine lines and wrinkles, improves skin texture, minimizes enlarged pores, and addresses pigmentation concerns including sun damage and age spots. Suitable for various skin types, the Moveo Glo Facial targets multiple signs of aging in a single treatment, revealing smoother, firmer, more youthful-looking skin.
                </p>
                <p>
                  During treatment, laser energy penetrates the skin to trigger your body's natural healing response and collagen remodeling. Most patients require 3-5 sessions spaced several weeks apart for optimal results. Progressive improvements continue for months as new collagen forms. The procedure is well-tolerated with minimal downtime—expect mild redness that resolves within 1-2 days. Schedule your Moveo Glo Facial consultation at Century Longevity & Aesthetics to restore your skin's natural radiance and achieve lasting rejuvenation.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Moveo Glo Facial Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-md mx-auto bg-white rounded-lg p-12 shadow-2xl">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
                <svg 
                  className="w-12 h-12 text-gold" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" 
                  />
                </svg>
              </div>
              
              <h3 className="text-2xl font-heading font-normal text-olive-darkest mb-3 tracking-wider">
                Moveo Glo Facial
              </h3>
              <p className="text-4xl font-heading font-light text-gold mb-2 tracking-tight">$250</p>
              <p className="text-lg font-body text-gray-600 mb-8">Per session</p>
              
              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full bg-olive-darkest text-white px-8 py-4 rounded-lg font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Moveo Glo Facial</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Revitalize your skin and reveal a more youthful, radiant complexion with our advanced Moveo Glo Facial treatment!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about Moveo Glo Facial treatments, what to expect, and how we can help you achieve your skincare goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('skin-revitalization');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default SkinRevitalization;